import { drop, figure, flower, groundRing, moon, palm, petal, swirl } from '../kit';
import { type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ระบำเงา (dim-moon, yin): a mourning dance in long white sleeves, pale peach petals, dew and dusk mist. Re-themed to
 * quiet / mourning (10-13): no blood or poison green; drain and charm read as pale wisps and drifting mist.
 */

const SLEEVE = '#f4f1ea';
const SLEEVE_SHADE = '#c8c4d8';
const PETAL = '#f6d8dc';
const PETAL_DEEP = '#d8a8b8';
const MIST = '#8fa0c8';
const MOON = '#e8eef8';
const DEW = '#bfe0ff';
const DUSK = '#4a4668';

/** A long sleeve ribbon along the points, `w` wide, its hem lit. */
function sleeve(p: Pix, pts: readonly (readonly [number, number])[], w = 3): void {
  p.path(pts, SLEEVE_SHADE, w);
  p.path(pts, SLEEVE, Math.max(1, w - 2));
}

function petals(p: Pix, list: readonly (readonly [number, number])[]): void {
  list.forEach(([x, y], k) => petal(p, x, y, k % 2 ? PETAL_DEEP : PETAL));
}

registerIcons('skill', {
  // The sleeve flicks out like a whip, a petal shaken loose at its hem.
  'dd-sleeve': (p) => {
    sleeve(p, [
      [3, 26],
      [9, 18],
      [8, 11],
      [14, 6],
      [21, 9],
      [27, 6],
    ], 4);
    petals(p, [
      [24, 14],
      [27, 19],
    ]);
  },
  // Turning in place, then the palm: a ring of sleeve round a striking hand.
  'dd-twirl': (p) => {
    p.ring(16, 16, 12, 6, SLEEVE_SHADE, 0, Math.PI * 2);
    p.ring(16, 15, 12, 6, SLEEVE, Math.PI, Math.PI * 2);
    palm(p, 10, 8, '#f2dcd0');
  },
  // Three turns of petals round the dancer.
  'dd-petals': (p) => {
    swirl(p, 16, 18, 13, PETAL_DEEP, { arms: 3, turn: 3.6, squash: 0.45 });
    p.ell(16, 12, 4, 6, '#2a2440');
    figure(p, 13, 7, SLEEVE, 'stand');
    petals(p, [
      [3, 6],
      [26, 5],
      [4, 25],
      [25, 25],
    ]);
  },
  // Gliding in and drawing the breath out: pale wisps flowing to an open hand.
  'dd-drain': (p) => {
    for (let k = 0; k < 3; k++)
      p.path(
        [
          [28, 8 + k * 7],
          [22, 10 + k * 6],
          [16, 9 + k * 5],
          [11, 13 + k * 3],
        ],
        k === 1 ? MOON : MIST,
        1,
      );
    palm(p, 1, 9, '#f2dcd0', { flip: true });
  },
  // A sweet mist laid round the caster, petals adrift in it.
  'dd-mist': (p) => {
    for (const [x, y, r, c] of [
      [9, 19, 6, MIST],
      [22, 19, 6, MIST],
      [16, 16, 7, SLEEVE_SHADE],
      [11, 13, 4, SLEEVE_SHADE],
      [20, 12, 4, SLEEVE],
    ] as const)
      p.ell(x, y, r, r * 0.7, c);
    groundRing(p, 16, 26, 13, 4, MIST);
    petals(p, [
      [6, 8],
      [23, 6],
      [15, 4],
    ]);
  },
  // The trance: a dancer under a slow moon, sleeves trailing in rings.
  'dd-trance': (p) => {
    moon(p, 24, 7, 5, MOON);
    figure(p, 12, 12, SLEEVE, 'stand');
    sleeve(
      p,
      [
        [12, 15],
        [6, 18],
        [5, 24],
        [10, 27],
      ],
      2,
    );
    sleeve(
      p,
      [
        [19, 15],
        [25, 17],
        [27, 23],
        [22, 27],
      ],
      2,
    );
  },
  // Drinking dew from a peach blossom.
  'dd-dew': (p) => {
    flower(p, 14, 12, 9, PETAL, PETAL_DEEP);
    drop(p, 22, 20, 2.5, DEW);
    drop(p, 18, 26, 1.6, DEW);
    p.rect(8, 22, 2, 7, '#7a8a5a');
  },
  // The shadow chorus: a ring of dancers under the moon.
  'dd-heaven': (p) => {
    moon(p, 16, 7, 5, MOON);
    p.ell(16, 23, 14, 5, DUSK);
    for (const [x, y, c] of [
      [2, 17, MIST],
      [9, 19, SLEEVE],
      [17, 19, SLEEVE],
      [24, 17, MIST],
    ] as const)
      figure(p, x, y, c, 'stand');
    petals(p, [
      [5, 6],
      [26, 12],
    ]);
  },
});
